import { useBehaviorSystem } from 'contexts/behaviors';
import { useCore } from 'contexts/core';
import { usePlannerConfig } from 'contexts/plannerConfig';
import { resetLayout, setAgeGroup, showProductMenuHeader } from 'ducks/layout';
import { getSelectedGroupId, resetAllFilters, selectGroup } from 'ducks/products';
import { useCallback, useEffect } from 'react';
import { useSurvey } from 'contexts/survey';
import useRestartPlanner from './useRestartPlanner';
import { useAppDispatch } from 'ducks/hooks';
import {
  hideResumeLastSessionModal,
  resetFrontStyleSheet,
  setAcceptDeleteIncludedArticles
} from 'ducks/sheets';
import { defaultAgeGroup } from '@inter-ikea-storageone/range-smastad';

import { useSelector } from 'react-redux';
import { PlatsaSoftClosingHingeComponent } from '@inter-ikea-storageone/range-platsa';
import { isKiosk } from 'utils/platform';
import { getStoreId } from 'utils/queryUtils';
import { useServices } from 'contexts/services';

const useResetPlanner = (runEffect = true) => {
  const dispatch = useAppDispatch();
  const behaviourSystem = useBehaviorSystem();
  const coreApi = useCore();
  const { zipCodeService, storeService } = useServices();
  const { resetFeatures } = usePlannerConfig();
  const { resetSurvey } = useSurvey();
  const restartPlanner = useRestartPlanner();
  const scene = coreApi.entities.getScene();
  const selectedGroupId = useSelector(getSelectedGroupId);

  const resetPlanner = useCallback(() => {
    dispatch(resetLayout());
    restartPlanner();

    // Only trigger exit if a product group is active
    if (selectedGroupId)
      behaviourSystem.trigger(
        'productGroupExited',
        { groupId: selectedGroupId, menuType: 'main' },
        null
      );

    if (scene?.hasComponent(PlatsaSoftClosingHingeComponent.id)) {
      scene?.removeComponent(PlatsaSoftClosingHingeComponent.id);
    }
    dispatch(selectGroup(''));
    dispatch(showProductMenuHeader());
    dispatch(resetAllFilters());
    dispatch(resetFrontStyleSheet());
    dispatch(hideResumeLastSessionModal());
    dispatch(setAgeGroup(defaultAgeGroup));
    dispatch(setAcceptDeleteIncludedArticles(false));

    resetSurvey();
    resetFeatures();

    coreApi.camera.resetCamera?.();

    if (isKiosk()) {
      const storeId = getStoreId() || '';
      zipCodeService.setZipCode('');
      storeService.setStoreId(storeId);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (runEffect) resetPlanner();
  }, [runEffect, resetPlanner]);

  return resetPlanner;
};

export default useResetPlanner;
